import { GraphQLError } from 'graphql';
import { Observable, RequestParameters, Variables, SubscribeFunction } from 'relay-runtime';
import { createClient } from 'graphql-ws';
import serverVars from 'server-vars';

import { CreateFetcherArgs } from './client';
import { getClientHeaders } from './src/getClientHeaders';

type SetupSubscriptionArgs = {
    getQueries: CreateFetcherArgs['getQueries'];
    userType: CreateFetcherArgs['userType'];
};
type CloseEvent = {
    code: number;
};
const isCloseEvent = (errOrCloseEvent: unknown): errOrCloseEvent is CloseEvent => {
    return (errOrCloseEvent as CloseEvent).code !== undefined;
};

const NODE_GRAPHQL_SUBSCRIPTIONS_URL = serverVars.get('NODE_GRAPHQL_SUBSCRIPTIONS_URL');
const RECONNECTION_ATTEMPTS = 500;
const ERROR_CODE_FORBIDDEN = 4403;
const WS_KEEP_ALIVE = 5000;

export function setupSubscriptions({
    getQueries = () => ({}),
    userType,
}: SetupSubscriptionArgs): SubscribeFunction {
    const subscriptionsClient = createClient({
        url: NODE_GRAPHQL_SUBSCRIPTIONS_URL,
        retryAttempts: RECONNECTION_ATTEMPTS,
        lazy: true, // establish ws on first subscribe, close on last unsubscribe
        keepAlive: WS_KEEP_ALIVE,
        isFatalConnectionProblem(errOrCloseEvent) {
            if (isCloseEvent(errOrCloseEvent) && errOrCloseEvent.code === ERROR_CODE_FORBIDDEN) {
                return true; // fail immediatly - server does not allow ws connections
            }
            return false; // attempt reconnection
        },
    });

    // can be used for both fetch & subscribe
    return function fetchOrSubscribe(operation: RequestParameters, variables: Variables) {
        return Observable.create(sink => {
            if (!operation.text && !operation.id) {
                return sink.error(new Error('Operation must contain text or id'));
            }
            return subscriptionsClient.subscribe(
                {
                    operationName: operation.name,
                    query: operation.text || 'use persisted query',
                    variables,
                    // @ts-ignore - more explicit than looking at query field on server to determine if persisted query id
                    queryId: operation.id,
                    // @ts-ignore - send params on each new subscription (instead of using connectionParams) - allows updating token on auth change
                    subscriptionParams: {
                        headers: getClientHeaders(getQueries, userType),
                        originalUrl: window.location.href,
                    },
                },
                {
                    ...sink,
                    error: err => {
                        if (err instanceof Error) {
                            return sink.error(err);
                        }
                        if (err instanceof CloseEvent) {
                            return sink.error(
                                new Error(
                                    `Socket closed with event ${err.code} ${err.reason || ''}`
                                )
                            );
                        }
                        return sink.error(
                            new Error(
                                (err as GraphQLError[]).map(({ message }) => message).join(', ')
                            )
                        );
                    },
                }
            );
        });
    };
}
